import { Controller } from "@hotwired/stimulus"

// Pflegereform-2027-Rechner: Leistungen 2026 vs. PNOG-Entwurf 2027. Beträge kommen als JSON
// (PflegereformRechnerController#show), compute() spiegelt PflegereformRechner.berechne.
export default class extends Controller {
  static values = { data: Object }

  static targets = [
    "pflegegradButton",
    "wohnformRadio",
    "zuhauseFields",
    "heimFields",
    "pflegedienstOption",
    "pflegedienstCheckbox",
    "pflegedienstField",
    "pflegedienstInput",
    "pflegedienstVollCheckbox",
    "sachHint",
    "alltagCheckbox",
    "alltagField",
    "alltagInput",
    "umwandlungRow",
    "vpFieldset",
    "vpRadio",
    "vpField",
    "vpRange",
    "vpInput",
    "unter25Checkbox",
    "besitzstandField",
    "besitzstandCheckbox",
    "pg1Note",

    "zuhauseTable",
    "heimTable",
    "cell",

    "resultMonat",
    "resultJahr",
    "verdict",
    "resultSummary",
    "stickyBar",
    "stickyMonat"
  ]

  connect() {
    this.euro = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const pressed = this.pflegegradButtonTargets.find(btn => btn.getAttribute("aria-pressed") === "true")
    this.pflegegrad = pressed ? parseInt(pressed.dataset.value) : 3
    this.calculate()
    this.observeResultCard()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  observeResultCard() {
    if (!this.hasStickyBarTarget || !this.hasResultSummaryTarget || !("IntersectionObserver" in window)) return
    this.observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting
      this.stickyBarTarget.classList.toggle("translate-y-full", !show)
      this.stickyBarTarget.inert = !show
    })
    this.observer.observe(this.resultSummaryTarget)
  }

  selectPflegegrad(event) {
    this.pflegegrad = parseInt(event.currentTarget.dataset.value)
    this.pflegegradButtonTargets.forEach(btn => {
      const selected = parseInt(btn.dataset.value) === this.pflegegrad
      btn.setAttribute("aria-pressed", selected)
      btn.classList.toggle("bg-ph24_new_blue", selected)
      btn.classList.toggle("text-white", selected)
      btn.classList.toggle("bg-ph24_new_gray_dark", !selected)
      btn.classList.toggle("text-ph24_new_blue", !selected)
      btn.classList.toggle("hover:bg-ph24_new_blue/15", !selected)
    })
    this.calculate()
  }

  calculate() {
    const wohnform = this.wohnformRadioTargets.find(r => r.checked)?.value || "zuhause"
    const heim = wohnform === "heim"
    const d = this.dataValue
    // Sachleistung gibt es erst ab Pflegegrad 2
    const dienst = this.pflegegrad >= 2 && this.pflegedienstCheckboxTarget.checked
    const voll = dienst && this.pflegedienstVollCheckboxTarget.checked
    const alltag = this.alltagCheckboxTarget.checked

    this.zuhauseFieldsTarget.classList.toggle("hidden", heim)
    this.heimFieldsTarget.classList.toggle("hidden", !heim)
    this.pflegedienstOptionTarget.classList.toggle("hidden", this.pflegegrad < 2)
    this.pflegedienstFieldTarget.classList.toggle("hidden", !dienst)
    this.pflegedienstInputTarget.disabled = voll
    this.alltagFieldTarget.classList.toggle("hidden", !alltag)
    if (this.pflegegrad >= 2) {
      const tausend = new Intl.NumberFormat("de-DE")
      this.sachHintTarget.textContent = `(${tausend.format(d.sachleistung[2026][this.pflegegrad])} / ${tausend.format(d.sachleistung[2027][this.pflegegrad])} €)`
    }
    this.besitzstandFieldTarget.classList.toggle("hidden", !(heim && this.pflegegrad === 1))
    this.pg1NoteTarget.classList.toggle("hidden", heim || this.pflegegrad !== 1)
    // Verhinderungspflege gibt es erst ab Pflegegrad 2
    const vpJa = this.vpRadioTargets.find(r => r.checked)?.value === "ja"
    this.vpFieldsetTarget.classList.toggle("hidden", this.pflegegrad < 2)
    this.vpFieldTarget.classList.toggle("hidden", !vpJa)
    this.zuhauseTableTarget.classList.toggle("hidden", heim)
    this.heimTableTarget.classList.toggle("hidden", !heim)

    const eingaben = {
      pflegegrad: this.pflegegrad,
      pflegedienst: dienst ? this.num(this.pflegedienstInputTarget) : 0,
      pflegedienstVoll: voll,
      alltagshilfe: alltag ? this.num(this.alltagInputTarget) : 0,
      vpGeplant: vpJa ? this.num(this.vpInput("geplant")) : 0,
      vpAkut: vpJa ? this.num(this.vpInput("akut")) : 0,
      unter25: this.unter25CheckboxTarget.checked,
      besitzstand: this.besitzstandCheckboxTarget.checked
    }
    // Beide Tabellen tragen immer ihr eigenes Szenario, auch die gerade versteckte
    const ergebnisse = {
      zuhause: this.compute({ ...eingaben, wohnform: "zuhause" }),
      heim: this.compute({ ...eingaben, wohnform: "heim" })
    }
    const r = ergebnisse[wohnform]

    this.cellTargets.forEach(cell => {
      const [jahr, key] = cell.dataset.key.split("-")
      cell.textContent = this.euro.format(ergebnisse[cell.dataset.scope][jahr][key] ?? 0)
    })
    // Umwandlungszeile nur, wenn 2026 Sachleistung für Alltagshilfe genutzt wird
    this.umwandlungRowTarget.classList.toggle("hidden", !(ergebnisse.zuhause[2026].umwandlung > 0))
    // Monat = Jahr / 12, Verhinderungspflege anteilig: sonst passen die beiden Kacheln scheinbar nicht zusammen
    this.resultMonatTarget.textContent = this.signed(r.differenzJahr / 12)
    this.resultJahrTarget.textContent = this.signed(r.differenzJahr)
    if (this.hasStickyMonatTarget) this.stickyMonatTarget.textContent = this.signed(r.differenzJahr / 12)
    this.verdictTarget.textContent = this.verdict(r, heim)
  }

  // Regler und Eingabefeld je Art (geplant/akut) halten denselben Jahresbetrag
  updateVpFromRange(event) {
    const kind = event.currentTarget.dataset.kind
    const range = this.vpRange(kind)
    range.value = Math.min(range.value, this.vpMax(kind))
    this.vpInput(kind).value = new Intl.NumberFormat("de-DE").format(range.value)
    this.calculate()
  }

  updateVpFromInput(event) {
    const kind = event.currentTarget.dataset.kind
    const input = this.vpInput(kind)
    const max = this.vpMax(kind)
    // Über dem Jahresbetrag aufs Maximum setzen, sonst rechnet compute() still mit weniger als angezeigt
    if (this.num(input) > max) input.value = new Intl.NumberFormat("de-DE").format(max)
    this.vpRange(kind).value = Math.min(this.num(input), max)
    this.calculate()
  }

  // Geplant und akut teilen sich 2026 den Gemeinsamen Jahresbetrag
  vpMax(kind) {
    const andere = kind === "geplant" ? "akut" : "geplant"
    return this.dataValue.jahresbetrag_2026 - Math.min(this.num(this.vpInput(andere)), this.dataValue.jahresbetrag_2026)
  }

  vpInput(kind) {
    return this.vpInputTargets.find(el => el.dataset.kind === kind)
  }

  vpRange(kind) {
    return this.vpRangeTargets.find(el => el.dataset.kind === kind)
  }

  compute({ pflegegrad, wohnform, pflegedienst, pflegedienstVoll, alltagshilfe, vpGeplant, vpAkut, unter25, besitzstand }) {
    const d = this.dataValue
    if (wohnform === "heim") return this.heim(pflegegrad, besitzstand)

    // 2026 teilen sich beide den Jahresbetrag, 2027 trägt das Überbrückungsbudget nur die akute
    const clamp = (wert, max) => Math.min(Math.max(wert, 0), max)
    const geplant = pflegegrad >= 2 ? clamp(vpGeplant, d.jahresbetrag_2026) : 0
    const akut = pflegegrad >= 2 ? clamp(vpAkut, d.jahresbetrag_2026 - geplant) : 0
    const vp = { 2026: geplant + akut, 2027: Math.min(akut, d.ueberbrueckungsbudget) }
    const jahre = {}
    for (const jahr of [2026, 2027]) {
      const pgAb2 = pflegegrad >= 2
      const sach = pgAb2 ? d.sachleistung[jahr][pflegegrad] : 0
      const geld = pgAb2 ? d.pflegegeld[jahr][pflegegrad] : 0
      const gedeckt = pflegedienstVoll ? sach : clamp(pflegedienst, sach)
      const entlastung = jahr === 2026 ? d.entlastung_2026 : (pgAb2 ? (unter25 ? d.sozialraumbudget_unter_25 : d.sozialraumbudget) : 0)
      // Alltagshilfe zahlt zuerst der Entlastungsbetrag; den Rest 2026 die Umwandlung aus dem ungenutzten Sachleistungsrest
      const umwandlung = jahr === 2026 ? Math.min(Math.max(alltagshilfe - entlastung, 0), sach * d.umwandlung_anteil, sach - gedeckt) : 0
      const anteil = sach > 0 ? (gedeckt + umwandlung) / sach : 0
      const pflegegeld = geld * (1 - anteil)
      const verbrauch = jahr === 2026 ? d.verbrauch_2026 : 0
      const verhinderung = vp[jahr]
      const kurzzeit = pgAb2 ? (jahr === 2026 ? d.jahresbetrag_2026 : d.ueberbrueckungsbudget) - verhinderung : 0
      const monat = pflegegeld + gedeckt + umwandlung + entlastung + verbrauch
      jahre[jahr] = { pflegegeld, pflegedienst: gedeckt, umwandlung, entlastung, verbrauch, monat, verhinderung, kurzzeit, jahr: monat * 12 + verhinderung }
    }
    return { ...jahre, differenzMonat: jahre[2027].monat - jahre[2026].monat, differenzJahr: jahre[2027].jahr - jahre[2026].jahr }
  }

  heim(pflegegrad, besitzstand) {
    const d = this.dataValue
    const jahre = {}
    for (const jahr of [2026, 2027]) {
      const betrag = pflegegrad >= 2 ? d.stationaer[pflegegrad] : ((jahr === 2026 || besitzstand) ? d.stationaer_pg1_2026 : 0)
      jahre[jahr] = { stationaer: betrag, monat: betrag, jahr: betrag * 12 }
    }
    return { ...jahre, differenzMonat: jahre[2027].monat - jahre[2026].monat, differenzJahr: jahre[2027].jahr - jahre[2026].jahr }
  }

  verdict(r, heim) {
    if (heim) {
      if (r.differenzMonat < 0) return "Neue Heimbewohner mit Pflegegrad 1 erhalten ab 2027 keinen Zuschuss mehr. Wer ihn 2026 schon bezieht, behält ihn."
      return "Im Pflegeheim ändern sich die Leistungen der Pflegekasse laut Entwurf nicht: Leistungsbetrag und Zuschlag zum Eigenanteil bleiben gleich."
    }
    const monat = this.mehrWeniger(r.differenzMonat)
    const jahr = this.mehrWeniger(r.differenzJahr)
    if (Math.round(r.differenzJahr * 100) === 0) return "Laut Entwurf ändert sich für Sie unterm Strich nichts."
    if (r[2026].verhinderung - r[2027].verhinderung > 0) {
      // Laufende Leistungen und wegfallende Verhinderungspflege getrennt ausweisen
      const laufend = Math.round(r.differenzMonat * 100) === 0 ? "Ihre laufenden Leistungen bleiben gleich." : `Laufend erhalten Sie ${monat} pro Monat, aufs Jahr ${this.mehrWeniger(r.differenzMonat * 12)}.`
      return `${laufend} ${this.vpSatz(r)} Unterm Strich haben Sie ${jahr} im Jahr.${this.umwandlungSatz(r)}`
    }
    const kurzzeit = r[2027].kurzzeit < r[2026].kurzzeit ? ` Der Topf für Kurzzeitpflege schrumpft aber von ${this.euro.format(r[2026].kurzzeit)} auf ${this.euro.format(r[2027].kurzzeit)} im Jahr.` : ""
    return `Laut Entwurf erhalten Sie ${monat} pro Monat und ${jahr} im Jahr.${kurzzeit}${this.umwandlungSatz(r)}`
  }

  // Umwandlung für Alltagshilfe entfällt 2027; das höhere Pflegegeld gleicht das nur teilweise aus
  umwandlungSatz(r) {
    if (!(r[2026].umwandlung > 0)) return ""
    return ` Die ${this.euro.format(r[2026].umwandlung)} Sachleistung für Ihre Alltagshilfe entfallen 2027.`
  }

  // Wie viel der Ersatzpflege 2027 wegfällt und wie viel das Überbrückungsbudget trägt
  vpSatz(r) {
    const weg = this.euro.format(r[2026].verhinderung - r[2027].verhinderung)
    const wort = Math.round(r.differenzMonat * 100) >= 0 ? "aber" : "zusätzlich"
    if (r[2027].verhinderung <= 0) return `Ihre Verhinderungspflege von ${weg} fällt ${wort} als eigene Leistung weg.`
    return `Von Ihrer Verhinderungspflege übernimmt das Überbrückungsbudget ${this.euro.format(r[2027].verhinderung)} für akute Fälle, ${weg} fallen ${wort} als eigene Leistung weg.`
  }

  mehrWeniger(betrag) {
    return `${this.euro.format(Math.abs(Math.round(betrag * 100) / 100))} ${betrag >= 0 ? "mehr" : "weniger"}`
  }

  signed(betrag) {
    const rund = Math.round(betrag * 100) / 100
    if (rund === 0) return this.euro.format(0)
    return `${rund > 0 ? "+" : "−"}${this.euro.format(Math.abs(rund))}`
  }

  // Deutsche Schreibweise: „1.200“, „600,50“
  num(input) {
    const raw = (input.value || "").replace(/[\s€]/g, "")
    let normalized
    if (raw.includes(",")) normalized = raw.replace(/\./g, "").replace(",", ".")
    else if (/^\d{1,3}(\.\d{3})+$/.test(raw)) normalized = raw.replace(/\./g, "")
    else normalized = raw
    const value = parseFloat(normalized)
    return isNaN(value) ? 0 : Math.max(value, 0)
  }
}
